// src/components/ClientFilesModal.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal, Button, Form, Row, Col, Table, InputGroup, Alert, ProgressBar, Pagination, Badge
} from 'react-bootstrap';
import clientFileService from '../services/clientFileService';
import clientService from '../services/clientService';

const BYTES = v => Number(v || 0);
const fmtMB = b => (BYTES(b) / (1024 * 1024)).toFixed(2) + ' MB';

const normalize = (s) => (s || '').normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase().trim();

export default function ClientFilesModal({ clientId, show, onHide }) {
  const [client, setClient] = useState(null);

  const [rows, setRows] = useState([]);     // files list
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState('');
  const [mimeFilter, setMimeFilter] = useState(''); // '' | 'image/' | 'application/pdf' | 'other'

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // upload state
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // storage info
  const [storage, setStorage] = useState({ quotaBytes: 0, usedBytes: 0, freeBytes: 0 });

  useEffect(() => {
    if (!show || !clientId) return;
    setError(''); setSuccess(''); setProgress(0);
    // client info (nom/prénom)
    clientService.get(clientId).then(setClient).catch(() => setClient(null));
    // storage
    clientFileService.storageInfo().then(setStorage).catch(() => { });
    // list page 1
    load(1, pageSize, search, mimeFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, clientId]);

  const load = (p = page, ps = pageSize, q = search, mf = mimeFilter) => {
    setError('');
    clientFileService
      .list(clientId, { page: p, pageSize: ps, search: q, mime: mf === 'other' ? '' : mf })
      .then(({ rows, total }) => { setRows(rows); setTotal(total); setPage(p); setPageSize(ps); })
      .catch(err => setError(err.response?.data?.message || 'Erreur chargement des fichiers.'));
  };

  // recherche locale complémentaire (couvre toutes colonnes affichées)
  const filtered = useMemo(() => {
    const q = normalize(search);
    const mf = mimeFilter;
    return rows.filter(f => {
      const matchesMime =
        !mf ||
        (mf === 'other'
          ? (f.mimeType !== 'application/pdf' && !f.mimeType.startsWith('image/'))
          : (mf === 'image/' ? f.mimeType.startsWith('image/') : f.mimeType === mf));
      if (!matchesMime) return false;
      if (!q) return true;
      const hay = normalize([
        f.originalName, f.mimeType, f.size, new Date(f.createdAt).toLocaleString()
      ].join(' '));
      return hay.includes(q);
    });
  }, [rows, search, mimeFilter]);

  // Pagination locale (sur la page courante)
  const totalLocal = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalLocal / pageSize));
  const current = Math.min(page, totalPages);
  const start = (current - 1) * pageSize;
  const visible = useMemo(() => filtered.slice(start, start + pageSize), [filtered, start, pageSize]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(''); setSuccess('');
    // pré-check quota
    if (BYTES(file.size) > BYTES(storage.freeBytes)) {
      setError(`Espace insuffisant. Libre: ${fmtMB(storage.freeBytes)}, Fichier: ${fmtMB(file.size)}`);
      e.target.value = '';
      return;
    }
    try {
      setUploading(true); setProgress(0);
      await clientFileService.upload(clientId, file, p => setProgress(p));
      setSuccess('Fichier uploadé avec succès.');
      // rafraîchir liste & storage
      clientFileService.storageInfo().then(setStorage);
      load(1, pageSize, search, mimeFilter);
    } catch (err) {
      const msg = err.response?.data?.message || 'Échec de l’upload.';
      setError(msg);
    } finally {
      setUploading(false);
      e.target.value = '';
      setTimeout(() => setSuccess(''), 2500);
    }
  };

  const onDownload = async (fileId, fallbackName) => {
    setError(''); setSuccess('');
    try {
      const { blob, filename } = await clientFileService.download(clientId, fileId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || fallbackName || 'fichier';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.message || 'Téléchargement impossible.');
    }
  };

  const onDelete = (id) => {
    if (!window.confirm('Supprimer ce fichier ?')) return;
    setError(''); setSuccess('');
    clientFileService.remove(clientId, id)
      .then(() => {
        setSuccess('Fichier supprimé.');
        clientFileService.storageInfo().then(setStorage);
        load(page, pageSize, search, mimeFilter);
        setTimeout(() => setSuccess(''), 2000);
      })
      .catch(err => setError(err.response?.data?.message || 'Suppression impossible.'));
  };

  const renderPagination = () => (
    <div className="d-flex justify-content-between align-items-center mt-2">
      <small className="text-muted">
        Espace libre: <b>{fmtMB(storage.freeBytes)}</b> / Quota: {fmtMB(storage.quotaBytes)}
      </small>
      <div className="d-flex align-items-center">
        <Form.Select
          size="sm"
          value={pageSize}
          onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
          className="me-2"
          style={{ width: 90 }}
        >
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={20}>20</option>
        </Form.Select>
        <Pagination className="mb-0">
          <Pagination.Prev disabled={current === 1} onClick={() => setPage(p => Math.max(1, p - 1))} />
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(p => p === 1 || p === totalPages || Math.abs(p - current) <= 1)
            .map((p, idx, arr) => {
              const prev = arr[idx - 1];
              const needEllipsis = prev && p - prev > 1;
              return (
                <React.Fragment key={p}>
                  {needEllipsis && <Pagination.Ellipsis disabled />}
                  <Pagination.Item active={p === current} onClick={() => setPage(p)}>{p}</Pagination.Item>
                </React.Fragment>
              );
            })}
          <Pagination.Next disabled={current === totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} />
        </Pagination>
      </div>
    </div>
  );

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          Gestion des fichiers — {client ? `${client.firstName} ${client.lastName} (${client.nifCin})` : `Client #${clientId}`}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}
        {success && <Alert variant="success" className="py-2">{success}</Alert>}

        {/* Upload */}
        <Row className="align-items-end g-3 mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Uploader un fichier (PDF/Images)</Form.Label>
              <Form.Control
                type="file"
                accept="application/pdf,image/*"
                disabled={uploading}
                onChange={onUpload}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            {uploading && (
              <>
                <div className="mb-1 small">Téléversement en cours… {progress}%</div>
                <ProgressBar now={progress} animated striped />
              </>
            )}
          </Col>
        </Row>

        {/* Filtres & recherche */}
        <Row className="align-items-center g-2 mb-3">
          <Col md={6}>
            <InputGroup>
              <Form.Control
                placeholder="Rechercher (nom, type, date, taille)…"
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
              />
              <Button variant="outline-secondary" onClick={() => { setSearch(''); setPage(1); }}>×</Button>
            </InputGroup>
          </Col>
          <Col md={3}>
            <Form.Select
              value={mimeFilter}
              onChange={e => { setMimeFilter(e.target.value); setPage(1); }}
            >
              <option value="">Tous les types</option>
              <option value="image/">Images</option>
              <option value="application/pdf">PDF</option>
              <option value="other">Autres</option>
            </Form.Select>
          </Col>
          <Col md={3} className="text-md-end">
            <Badge bg={storage.freeBytes > 0 ? 'success' : 'danger'}>
              Libre: {fmtMB(storage.freeBytes)}
            </Badge>
          </Col>
        </Row>

        {/* Table */}
        <Table hover responsive>
          <thead>
            <tr>
              <th>Nom</th>
              <th>Type</th>
              <th>Taille</th>
              <th>Ajouté le</th>
              <th style={{ width: 220 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.map(f => (
              <tr key={f.id}>
                <td>{f.originalName}</td>
                <td>{f.mimeType}</td>
                <td>{fmtMB(f.size)}</td>
                <td>{new Date(f.createdAt).toLocaleString()}</td>
                <td>
                  <Button
                    size="sm"
                    variant="outline-primary"
                    className="me-2"
                    onClick={() => onDownload(f.id, f.originalName)}
                  >
                    Télécharger
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => onDelete(f.id)}
                  >
                    Supprimer
                  </Button>
                </td>
              </tr>
            ))}
            {visible.length === 0 && (
              <tr>
                <td colSpan="5" className="text-center text-muted">Aucun fichier.</td>
              </tr>
            )}
          </tbody>
        </Table>

        {visible.length > 0 && renderPagination()}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Fermer</Button>
      </Modal.Footer>
    </Modal>
  );
}
